import * as React from "react";
import {Form, Modal} from "antd";
import {FormComponentProps} from "antd/lib/form";
import locales from '../locales';
import {getAmountTypeFromOverrideOrItem, InvoiceEntry} from "../api/dtos/summaries";
import InputFormItem from "../common/form-items/InputFormItem";
import NumberFormItem from "../common/form-items/NumberFormItem";
import SelectFormItem from "../common/form-items/SelectFormItem";
import TextAreaFormItem from "../common/form-items/TextAreaFormItem";
import {amountTypesForInvoice, getAmountUnitForAmountType} from "../common/AmountType";
import {getVatOptions, VAT} from "../common/vat";
import {numberGroupFormatter, numberGroupParser} from "../utils/number-utils";
import AutoCompleteFormItem from "../common/form-items/AutoCompleteFormItem";

const FIELD_NAME = 'name';
const FIELD_AMOUNT = 'amount';
const FIELD_AMOUNT_TYPE = 'amountType';
const FIELD_PRICE = 'price';
const FIELD_VAT = 'vat';
const FIELD_COMMENT = 'comment';

interface Props extends FormComponentProps {
  invoiceEntry: InvoiceEntry | undefined;
  onSave: (entry: InvoiceEntry) => void;
  onCancel: () => void;
}

class InvoiceEntryEditModal extends React.Component<Props, {}> {

  public render(): React.ReactNode {
    const {invoiceEntry, onCancel, form} = this.props;

    return (
      <Modal
        title={locales.edit_entry}
        visible={invoiceEntry !== undefined}
        onCancel={onCancel}
        onOk={this.onSaveClick}
        okText={locales.save}
        cancelText={locales.cancel}
        destroyOnClose={true}
      >
        {invoiceEntry && (
          <Form>
            <InputFormItem
              label={locales.entry_name}
              fieldId={FIELD_NAME}
              form={form}
              initialValue={invoiceEntry.name}
              required={true}
            />

            <NumberFormItem
              label={locales.amount}
              fieldId={FIELD_AMOUNT}
              form={form}
              initialValue={invoiceEntry.amount}
              required={true}
              step={0.25}
              precision={2}
              hideIntegerDecimals={true}
              formatter={numberGroupFormatter}
              parser={numberGroupParser}
            />

            <AutoCompleteFormItem
              label={locales.amount_type}
              fieldId={FIELD_AMOUNT_TYPE}
              form={form}
              initialValue={getAmountUnitForAmountType(getAmountTypeFromOverrideOrItem(invoiceEntry)) ?? invoiceEntry.amountType}
              required={true}
              validateOnDataSource={false}
              dataSource={amountTypesForInvoice}
            />

            <NumberFormItem
              label={locales.net_unit_price}
              fieldId={FIELD_PRICE}
              form={form}
              initialValue={invoiceEntry.price}
              required={true}
              step={0.01}
              precision={2}
              parser={numberGroupParser}
              hideIntegerDecimals={true}
              rules={[
                {type: 'number', message: locales.invalid_input},
                {validator: (_, value, callback) => callback(value === 0 ? locales.net_unit_price_non_zero : undefined)},
              ]}
              formatter={numberGroupFormatter}
            >
              <> {locales.app_currency}</>
            </NumberFormItem>

            <SelectFormItem
              label={locales.vat}
              fieldId={FIELD_VAT}
              form={form}
              initialValue={invoiceEntry.vat || VAT.P27}
              required={true}
              options={getVatOptions()}
            />

            <TextAreaFormItem
              label={locales.entry_comment}
              fieldId={FIELD_COMMENT}
              form={form}
              initialValue={invoiceEntry.comment}
              required={false}
              maxLength={255}
            />
          </Form>
        )}
      </Modal>
    );
  }

  private onSaveClick = () => {
    const {invoiceEntry, form, onSave} = this.props;
    if (!invoiceEntry) {
      return;
    }
    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        onSave({
          ...invoiceEntry,
          name: values[FIELD_NAME],
          amount: values[FIELD_AMOUNT],
          amountType: values[FIELD_AMOUNT_TYPE],
          price: values[FIELD_PRICE],
          vat: values[FIELD_VAT],
          comment: values[FIELD_COMMENT] || null,
        });
      }
    });
  };
}

export default Form.create<Props>()(InvoiceEntryEditModal);



// WEBPACK FOOTER //
// ./src/invoicing/InvoiceEntryEditModal.tsx